import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLang } from '../i18n/LanguageContext';
import {
  ATTD_2026_VIDEO,
  CONGRESS_COVERAGE_VIDEO,
  DUBAI_LISBON_VIDEO,
} from '../data/portfolioData';
import VideoModal from './VideoModal';
import styles from './Services.module.css';

function PlayIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="19" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
      <path d="M17 14l12 6-12 6V14z" fill="rgba(255,255,255,0.9)" />
    </svg>
  );
}

const ICONS = {
  camera: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h3l1.5-2h7L17 7h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  booth: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9h16v10H4V9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M4 9l2-4h12l2 4M9 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  clock: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 7v5.5l3.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
};

const SERVICES = [
  {
    id: 'conference-coverage',
    icon: 'camera',
    title: { en: 'Conference & Congress Coverage', de: 'Konferenz- & Kongress-Berichterstattung' },
    description: {
      en:
        'Full-service video production for multi-day conferences, medical congresses, and industry summits — keynotes, panels, interviews, and atmosphere: everything that makes an event worth remembering. We also run fireside chats and podcast recordings on location, with lighting, sound, and camera set up in a format ready for distribution, no studio required. We move fast between stages and know how to stay invisible while capturing everything that matters.',
      de:
        'Full-Service-Videoproduktion für mehrtägige Konferenzen, medizinische Kongresse und Branchen-Summits — Keynotes, Panels, Interviews und Atmosphäre: alles, was ein Event erinnerenswert macht. Wir führen auch Fireside Chats und Podcast-Aufnahmen vor Ort durch, mit Licht, Ton und Kamera in einem format, das verteilerfertig ist — kein Studio nötig. Wir bewegen uns schnell zwischen Bühnen und wissen, wie man unsichtbar bleibt, während alles Wichtige eingefangen wird.',
    },
    posterSrc: '/images/services/conference-coverage.png',
    videoSrc: CONGRESS_COVERAGE_VIDEO,
  },
  {
    id: 'booth-activations',
    icon: 'booth',
    title: { en: 'Booth Highlight Videos', de: 'Stand-Highlight-Videos' },
    description: {
      en:
        'High-end highlight films for exhibition booths and brand activations. We capture your booth experience (the product demos, the conversations, the atmosphere) and deliver a polished film that works as a standalone piece long after the event ends. Clients include Dubai Economy & Tourism, Cylus, and others.',
      de:
        'Hochwertige Highlight-Filme für Messestände und Marken-Aktivierungen. Wir fangen das Stand-Erlebnis ein (die Produkt-Demos, die Gespräche, die Atmosphäre) und liefern einen polierten Film, der lange nach dem Event als eigenständiges Stück funktioniert. Kund:innen sind unter anderem Dubai Economy & Tourism und Cylus.',
    },
    posterSrc: '/images/services/booth-videos.png',
    videoSrc: DUBAI_LISBON_VIDEO,
    exampleTitle: 'Dubai Booth @ WebSummit',
  },
  {
    id: 'on-location-delivery',
    icon: 'clock',
    title: { en: 'On-location Delivery', de: 'Vor-Ort-Zustellung' },
    description: {
      en:
        "Need content before tomorrow's keynote? We offer on-location editing with 24-hour turnaround as an add-on to your production: finished clips delivered while the event is still happening. Not every project needs it, but when yours does, we're built for it.",
      de:
        'Brauchen Sie Content vor der morgigen Keynote? Wir bieten Vor-Ort-Schnitt mit 24-Stunden-Zusage als Add-on zur Produktion: fertige Clips, geliefert während das Event noch läuft. Nicht jedes Projekt braucht das — aber wenn Ihres es braucht, sind wir dafür gemacht.',
    },
    posterSrc: '/images/services/24-hour-delivery.png',
    videoSrc: ATTD_2026_VIDEO,
    exampleTitle: 'ATTD 2026',
    featured: true,
  },
];

function ServiceThumb({ service, onPlay, lang }) {
  const { t } = useLang();
  const title = service.title[lang] ?? service.title.en;
  const content = (
    <>
      <img
        src={service.posterSrc}
        alt={`${title} service illustration`}
        className={styles.poster}
        loading="lazy"
        decoding="async"
      />
      <div className={styles.thumbOverlay} aria-hidden="true" />
      {service.videoSrc && (
        <div className={styles.thumbPlay}>
          <PlayIcon />
          <span className={styles.watchLabel}>{t('services.watchExample')}</span>
        </div>
      )}
    </>
  );

  if (service.videoSrc) {
    return (
      <button
        type="button"
        className={styles.videoThumb}
        onClick={() => onPlay(service)}
        aria-label={`${t('services.watchExample')}: ${service.exampleTitle ?? title}`}
      >
        {content}
      </button>
    );
  }

  return <div className={styles.videoThumb}>{content}</div>;
}

export default function Services() {
  const revealRef = useScrollReveal();
  const { t, lang, tr } = useLang();
  const [activePlayback, setActivePlayback] = useState(null);

  const handlePlay = (service) => {
    setActivePlayback({
      videoId: service.id,
      videoSrc: service.videoSrc,
      title: service.exampleTitle ?? (service.title[lang] ?? service.title.en),
    });
  };

  return (
    <section id="services" className={styles.section}>
      <div className={`container reveal ${styles.inner}`} ref={revealRef}>
        <p className="section-label">{t('services.eyebrow')}</p>
        <h2 className={styles.headline}>
          {tr(
            'Live event video: built for the pace of the conference floor.',
            'Live-Event-Video: gemacht für das Tempo der Konferenzhalle.',
          )}
        </h2>
        <p className={styles.subtext}>
          {tr(
            <>
              We don&apos;t arrive, film, and disappear. We become part of your team for the duration
              of the event: shooting, editing, and delivering while everything else is still happening.
              One camera or a full crew: we scale the team to the event, and you keep one point of
              contact.
            </>,
            <>
              Wir kommen nicht an, filmen und verschwinden. Wir werden für die Dauer des Events Teil
              Ihres Teams: drehen, schneiden und liefern, während alles andere noch läuft. Eine Kamera
              oder ein ganzes Team: wir skalieren entsprechend dem Event, und Sie behalten einen
              Ansprechpartner.
            </>,
          )}
        </p>

        <div className={styles.grid}>
          {SERVICES.map((service) => (
            <article
              key={service.id}
              className={`${styles.card} ${service.featured ? styles.featured : ''}`}
            >
              <ServiceThumb service={service} onPlay={handlePlay} lang={lang} />

              <div className={styles.cardBody}>
                {service.featured && (
                  <span className={styles.badge}>{t('services.onRequest')}</span>
                )}
                <div className={styles.icon}>{ICONS[service.icon]}</div>
                <h3 className={styles.title}>{service.title[lang] ?? service.title.en}</h3>
                <p className={styles.description}>{service.description[lang] ?? service.description.en}</p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <VideoModal
        isOpen={!!activePlayback?.videoSrc}
        onClose={() => setActivePlayback(null)}
        title={activePlayback?.title ?? ''}
        videoId={activePlayback?.videoId ?? ''}
        videoSrc={activePlayback?.videoSrc}
      />
    </section>
  );
}
